// src/components/CustomizeMembership/ImageEditor.js

import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { FabricJSCanvas, useFabricJSEditor } from 'fabricjs-react';
import './ImageEditor.css';

const ImageEditor = forwardRef(({ selectedImageUrl, overlayImageUrl, onObjectSelected = () => {}, onObjectDeselected = () => {}, onOverlayCleared = () => {} }, ref) => {
    const { editor, onReady } = useFabricJSEditor();
    const snapThreshold = 10;

    useImperativeHandle(ref, () => ({
        exportCanvasAsPNG() {
            return new Promise((resolve, reject) => {
                if (!editor?.canvas) return reject(new Error('Canvas reference is missing.'));
                try {
                    // This triggers the security check
                    const dataURL = editor.canvas.toDataURL({ format: 'png', quality: 1.0 });
                    fetch(dataURL).then(res => res.blob()).then(resolve).catch(reject);
                } catch (error) {
                    reject(error);
                }
            });
        },
        exportAndDownloadCanvasAsPNG() {
            if (!editor?.canvas) return;
            try {
                const dataURL = editor.canvas.toDataURL({ format: 'png', quality: 1.0 });
                const downloadLink = document.createElement('a');
                downloadLink.href = dataURL;
                downloadLink.download = 'custom_design.png';
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            } catch (e) {
                console.error("Download failed (Tainted Canvas):", e);
            }
        },
        addOverlayImage,
    }));

    // ... (handleObjectMoving, guideLines logic remains the same) ...
    const handleObjectMoving = e => {
        const object = e.target;
        if (Math.abs(object.left - editor.verticalGuideLine) < snapThreshold) {
            object.set({ left: editor.verticalGuideLine, originX: 'center' });
            showVerticalGuideLine();
        }
        if (Math.abs(object.top - editor.horizontalGuideLine) < snapThreshold) {
            object.set({ top: editor.horizontalGuideLine, originY: 'center' });
            showHorizontalGuideLine();
        }
        editor.canvas.renderAll();
    };

    const showVerticalGuideLine = () => {
        if (editor.vLine) editor.canvas.remove(editor.vLine);
        editor.vLine = new window.fabric.Line(
            [editor.verticalGuideLine, 0, editor.verticalGuideLine, editor.canvas.getHeight()],
            { stroke: '#808080', strokeWidth: 1, strokeDashArray: [4, 3], selectable: false, evented: false }
        );
        editor.canvas.add(editor.vLine);
        editor.canvas.renderAll();
    };

    const showHorizontalGuideLine = () => {
        if (editor.hLine) editor.canvas.remove(editor.hLine);
        editor.hLine = new window.fabric.Line(
            [0, editor.horizontalGuideLine, editor.canvas.getWidth(), editor.horizontalGuideLine],
            { stroke: '#808080', strokeWidth: 1, strokeDashArray: [4, 3], selectable: false, evented: false }
        );
        editor.canvas.add(editor.hLine);
        editor.canvas.renderAll();
    };

    const hideGuideLines = () => {
        if (editor.vLine) {
            editor.canvas.remove(editor.vLine);
            editor.vLine = null;
        }
        if (editor.hLine) {
            editor.canvas.remove(editor.hLine);
            editor.hLine = null;
        }
        editor.canvas.renderAll();
    };

    const handleObjectSelected = e => {
        onObjectSelected(e.selected?.[0]);
    };

    const handleObjectDeselected = () => {
        onObjectDeselected();
    };

    const handleDeleteKey = e => {
        if (!editor?.canvas) return;
        if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') return;

        const activeObject = editor.canvas.getActiveObject();
        if ((e.key === 'Delete' || e.key === 'Backspace') && activeObject) {
            editor.canvas.remove(activeObject);
            editor.canvas.renderAll();
            const remainingImages = editor.canvas.getObjects().filter(o => o.type === 'image');
            if (remainingImages.length === 0) {
                onOverlayCleared();
            }
        }
    };

    // CORRECCIÓN 1: Agregar crossOrigin anonymous a la imagen de fondo (bandera)
    const loadAndCenterImage = imageUrl => {
        if (!editor?.canvas) return;
        const img = new window.Image();
        img.crossOrigin = "anonymous"; // IMPORTANTE
        img.src = imageUrl;
        img.onload = () => {
            const canvas = editor.canvas;
            const scale = Math.min(canvas.width / img.width, canvas.height / img.height) * 1.5;
            editor.canvas.setBackgroundImage(
                imageUrl,
                editor.canvas.renderAll.bind(editor.canvas),
                {
                    originX: 'center',
                    originY: 'center',
                    top: canvas.height / 2,
                    left: canvas.width / 2,
                    scaleX: scale,
                    scaleY: scale,
                    crossOrigin: 'anonymous' // IMPORTANTE PARA FABRIC
                }
            );
        };
        img.onerror = () => console.error('Failed to load image:', imageUrl);
    };

    // CORRECCIÓN 2: Agregar crossOrigin anonymous a la imagen superpuesta (logo)
    const addOverlayImage = (imageUrl) => {
        if (!editor?.canvas || !imageUrl) return;

        clearAllOverlayImages();

        const img = new window.Image();
        img.crossOrigin = "anonymous"; // IMPORTANTE: Permite al canvas leer los pixeles si el server tiene headers CORS
        img.src = imageUrl;
        img.onload = () => {
            const canvas = editor.canvas;
            const maxImgSize = 250;
            const scale = Math.min(maxImgSize / img.width, maxImgSize / img.height);
            const overlay = new window.fabric.Image(img, {
                left: canvas.width / 2,
                top: canvas.height / 2,
                originX: 'center',
                originY: 'center',
                scaleX: scale,
                scaleY: scale,
                cornerSize: 10,
                hasRotatingPoint: true,
                rotatingPointOffset: 40,
                borderColor: '#c5900b',
                cornerColor: '#8a2909',
                transparentCorners: false,
                crossOrigin: 'anonymous' // IMPORTANTE PARA FABRIC
            });
            editor.canvas.add(overlay).setActiveObject(overlay);
            editor.canvas.renderAll();
        };
        img.onerror = () => console.error('Failed to load overlay image:', imageUrl);
    };

    const clearAllOverlayImages = () => {
        if (!editor?.canvas) return;
        const images = editor.canvas.getObjects().filter(o => o.type === 'image');
        images.forEach(img => editor.canvas.remove(img));
        editor.canvas.renderAll();
    };

    useEffect(() => {
        if (editor?.canvas) {
            editor.verticalGuideLine = editor.canvas.getWidth() / 2;
            editor.horizontalGuideLine = editor.canvas.getHeight() / 2;

            editor.canvas.on('object:moving', handleObjectMoving);
            editor.canvas.on('selection:created', handleObjectSelected);
            editor.canvas.on('selection:updated', handleObjectSelected);
            editor.canvas.on('selection:cleared', handleObjectDeselected);
            editor.canvas.on('mouse:up', hideGuideLines);
            document.addEventListener('keydown', handleDeleteKey);

            const parentDiv = document.querySelector('.editor-wrap');
            const canvasWidth = parentDiv.clientWidth;
            const canvasHeight = parentDiv.clientHeight;
            editor.canvas.setDimensions({ width: canvasWidth, height: canvasHeight });

            return () => {
                editor.canvas.off('object:moving', handleObjectMoving);
                editor.canvas.off('selection:created', handleObjectSelected);
                editor.canvas.off('selection:updated', handleObjectSelected);
                editor.canvas.off('selection:cleared', handleObjectDeselected);
                editor.canvas.off('mouse:up', hideGuideLines);
                document.removeEventListener('keydown', handleDeleteKey);
                hideGuideLines();
            };
        }
    }, [editor]);

    useEffect(() => {
        if (editor && selectedImageUrl) {
            loadAndCenterImage(selectedImageUrl);
        }
    }, [editor, selectedImageUrl]);

    useEffect(() => {
        // Nota: overlayImageUrl ahora vendrá con el proxy url desde el padre si es necesario
        if (editor && overlayImageUrl) {
            addOverlayImage(overlayImageUrl);
        }
    }, [editor, overlayImageUrl]);

    return <FabricJSCanvas className="imageEditorCanvas" onReady={onReady} />;
});

export default ImageEditor;